import React, { useState, useEffect, useMemo } from 'react';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useNotifications } from '../../hooks/useNotifications';
import { Organization } from '../../types/organization';
import { organizationService } from '../../services/organizationService';
import { 
  PlusIcon, 
  PencilSquareIcon, 
  TrashIcon, 
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import Modal from '../common/Modal';
import DeleteOrganizationModal from './DeleteOrganizationModal';
import { packageService } from '../../services/packageService';
import type { Package, OrganizationSubscription } from '../../types/package';
import LoadingScreen from '../common/LoadingScreen';
import OrganizationSettings from './OrganizationSettings';

interface OrganizationFormData {
  name: string;
}

interface OrganizationListProps {
  // Props eltávolítva, mert most belső state-tel dolgozunk
}

const ITEMS_PER_PAGE = 10;

const OrganizationList: React.FC<OrganizationListProps> = () => {
  const { organizations, loading, refetch } = useOrganizations();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [formData, setFormData] = useState<OrganizationFormData>({ name: '' });
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<string | null>(null);
  const [orgToDeleteObj, setOrgToDeleteObj] = useState<Organization | null>(null);
  const formRef = React.useRef<HTMLFormElement>(null);
  const { user } = useAuth();
  const { t } = useLanguage();
  const { createNotification } = useNotifications();

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  // Szuper-admin: előfizetés / csomag oszlop
  const isSuperAdmin = (user as any)?.role === 'super_admin';
  const [subsByOrg, setSubsByOrg] = useState<Record<string, OrganizationSubscription>>({});
  const [packagesById, setPackagesById] = useState<Record<string, Package>>({});
  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        const [subs, pkgs] = await Promise.all([packageService.getAllSubscriptions(), packageService.getAllPackages()]);
        const m: Record<string, OrganizationSubscription> = {};
        subs.forEach(sb => { const cur = m[sb.organizationId]; if (!cur || ((sb.status === 'active' || sb.status === 'trial') && !(cur.status === 'active' || cur.status === 'trial'))) m[sb.organizationId] = sb; });
        setSubsByOrg(m);
        setPackagesById(Object.fromEntries(pkgs.map(pk => [pk.id, pk])));
      } catch (e) { console.error('subscriptions load failed', e); }
    })();
  }, [isSuperAdmin]);
  const subChip = (orgId: string) => {
    const sb = subsByOrg[orgId];
    if (!sb) return <span className="text-xs text-gray-400">nincs előfizetés</span>;
    const pk = packagesById[sb.packageId];
    const cls = sb.status === 'active' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : sb.status === 'trial' ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-200';
    const label = { active: 'Aktív', trial: 'Próba', suspended: 'Felfüggesztve', inactive: 'Inaktív', cancelled: 'Lemondva' }[sb.status] || sb.status;
    return <span className="inline-flex items-center gap-1.5 text-xs"><span className="text-gray-800 dark:text-gray-200">{pk?.name || '–'}</span><span className={`px-1.5 h-5 inline-flex items-center rounded-full text-[11px] font-medium ${cls}`}>{label}</span></span>;
  };
  const [filterRole, setFilterRole] = useState<'all' | 'owner' | 'admin' | 'member'>('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Filtered and paginated organizations
  const filteredOrganizations = useMemo(() => {
    let filtered = organizations;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(org =>
        org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.taxNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.address?.city?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Role filter
    if (filterRole !== 'all') {
      filtered = filtered.filter(org => {
        if (filterRole === 'owner') return org.ownerId === user?.id;
        if (filterRole === 'admin') return org.users?.find(u => u.userId === user?.id)?.role === 'admin';
        if (filterRole === 'member') return org.ownerId !== user?.id && !org.users?.find(u => u.userId === user?.id && u.role === 'admin');
        return true;
      });
    }

    return filtered;
  }, [organizations, searchTerm, filterRole, user?.id]);

  // Pagination
  const totalPages = Math.ceil(filteredOrganizations.length / ITEMS_PER_PAGE);
  const paginatedOrganizations = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredOrganizations.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredOrganizations, currentPage]);

  // Reset to page 1 when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRole]);

  const handleOpenCreateModal = () => {
    setEditingOrg(null);
    setFormData({ name: '' });
    setIsCreateModalOpen(true);
  };

  const handleOpenDetailsModal = (org: Organization) => {
    setSelectedOrg(org);
    setIsDetailsModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setEditingOrg(null);
    setFormData({ name: '' });
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedOrg(null);
  };

  const handleOrganizationUpdate = (updatedOrg: Organization) => {
    setSelectedOrg(updatedOrg);
    refetch();
  };

  const handleSaveChanges = () => {
    // Trigger form submit in OrganizationSettings
    const formElement = document.querySelector('#org-settings-form') as HTMLFormElement;
    if (formElement) {
      formElement.requestSubmit();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingOrg) {
        const updatedOrg = {
          ...editingOrg,
          name: formData.name,
          updatedAt: new Date()
        };

        await organizationService.updateOrganization(editingOrg.id, updatedOrg);
        if (selectedOrg?.id === editingOrg.id) {
          setSelectedOrg(updatedOrg);
        }
        toast.success(t('common.messages.organization.updateSuccess'));

        // Értesítés létrehozása a szervezet tagjai számára
        try {
          const lang = t('common.language') === 'English' ? 'en' : 'hu';
          const translations = lang === 'en' 
            ? (await import('../../translations/en')).default.notifications
            : (await import('../../translations/hu')).default.notifications;
            
          const title = translations.subjects?.organizationUpdated 
            ? translations.subjects.organizationUpdated.replace('{{organizationName}}', formData.name)
            : `${lang === 'hu' ? 'Szervezet adatai frissültek' : 'Organization data updated'}: ${formData.name}`;
            
          const orgUsers = editingOrg.users || [];
          for (const orgUser of orgUsers) {
            await createNotification({
              type: 'system',
              title: title,
              message: `${lang === 'hu' ? 'A(z)' : 'The'} ${formData.name} ${lang === 'hu' ? 'szervezet adatai módosultak' : 'organization data has been updated'}.`,
              priority: 'low',
              userId: orgUser.userId,
              organizationId: editingOrg.id,
              createdBy: user?.id,
              createdByName: user?.displayName,
              actionUrl: '/settings'
            });
          }
        } catch (notifError) {
          console.error('Error creating notifications:', notifError);
        }
      } else {
        const newOrg = {
          name: formData.name,
          ownerId: user?.id || '',
          taxNumber: '',
          registrationNumber: '',
          address: {
            street: '',
            city: '',
            postalCode: '',
            country: ''
          },
          contact: {
            name: '',
            email: '',
            phone: '',
            position: ''
          },
          users: [],
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        const createdOrg = await organizationService.createOrganization(newOrg);
        toast.success(t('common.messages.organization.createdSuccess'));

        // Értesítés létrehozása a létrehozónak
        try {
          if (createdOrg && user?.id) {
            const lang = t('common.language') === 'English' ? 'en' : 'hu';
            const translations = lang === 'en' 
              ? (await import('../../translations/en')).default.notifications
              : (await import('../../translations/hu')).default.notifications;
              
            const title = translations.subjects?.organizationCreated 
              ? translations.subjects.organizationCreated.replace('{{organizationName}}', formData.name)
              : `${lang === 'hu' ? 'Új szervezet létrehozva' : 'New organization created'}: ${formData.name}`;
              
            await createNotification({
              type: 'system',
              title: title,
              message: `${lang === 'hu' ? 'Sikeresen létrehoztad a(z)' : 'You successfully created the'} ${formData.name} ${lang === 'hu' ? 'szervezetet' : 'organization'}.`,
              priority: 'medium',
              userId: user.id,
              organizationId: createdOrg.id,
              createdBy: user.id,
              createdByName: user.displayName,
              actionUrl: '/settings'
            });
          }
        } catch (notifError) {
          console.error('Error creating notification:', notifError);
        }
      }

      await refetch();
      handleCloseCreateModal();
    } catch (error) {
      console.error('Error submitting organization:', error);
      toast.error(t('common.messages.organization.createdError'));
    }
  };

  const handleDelete = async (orgId: string) => {
    const org = organizations.find(o => o.id === orgId) || null;
    setOrgToDeleteObj(org);
  };

  const confirmDelete = async () => {
    if (orgToDelete) {
      try {
        // Szervezet adatainak mentése értesítéshez
        const orgToDeleteData = organizations.find(org => org.id === orgToDelete);
        
        await organizationService.deleteOrganization(orgToDelete);
        await refetch();
        toast.success(t('common.messages.organization.deleteSuccess'));

        // Értesítés létrehozása a szervezet tagjai számára
        try {
          if (orgToDeleteData) {
            const lang = t('common.language') === 'English' ? 'en' : 'hu';
            const translations = lang === 'en' 
              ? (await import('../../translations/en')).default.notifications
              : (await import('../../translations/hu')).default.notifications;
              
            const title = translations.subjects?.organizationDeleted 
              ? translations.subjects.organizationDeleted.replace('{{organizationName}}', orgToDeleteData.name)
              : `${lang === 'hu' ? 'Szervezet törölve' : 'Organization deleted'}: ${orgToDeleteData.name}`;
              
            const orgUsers = orgToDeleteData.users || [];
            for (const orgUser of orgUsers) {
              await createNotification({
                type: 'system',
                title: title,
                message: `${lang === 'hu' ? 'A(z)' : 'The'} ${orgToDeleteData.name} ${lang === 'hu' ? 'szervezet törlésre került' : 'organization has been deleted'}.`,
                priority: 'high',
                userId: orgUser.userId,
                organizationId: orgToDelete,
                createdBy: user?.id,
                createdByName: user?.displayName
              });
            }
          }
        } catch (notifError) {
          console.error('Error creating notifications:', notifError);
        }
      } catch (error) {
        console.error('Error deleting organization:', error);
        toast.error(t('common.messages.organization.deleteError'));
      }
      setIsConfirmDeleteOpen(false);
      setOrgToDelete(null);
    }
  };

  const getRoleBadge = (org: Organization) => {
    if (org.ownerId === user?.id) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
          {t('organizations.roles.owner')}
        </span>
      );
    }
    if (org.users?.find(u => u.userId === user?.id)?.role === 'admin') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
          {t('organizations.roles.administrator')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
        {t('organizations.roles.member')}
      </span>
    );
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-4">
      {/* Header with search and actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {t('organizations.title')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {filteredOrganizations.length} {filteredOrganizations.length === 1 ? 'szervezet' : 'szervezet'}
          </p>
        </div>
        <button
          onClick={handleOpenCreateModal}
          className="crm-btn crm-btn-primary"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          {t('organizations.buttons.new')}
        </button>
      </div>

      {/* Search and Filter Bar */}
      <div className="crm-card p-3 sm:p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Keresés név, adószám vagy város alapján..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-9 pl-9 pr-3 text-sm border rounded-lg"
              />
            </div>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-4 w-4 text-gray-400" />
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value as typeof filterRole)}
              className="h-9 pl-3 pr-8 text-sm border rounded-lg"
            >
              <option value="all">Összes szerep</option>
              <option value="owner">Tulajdonos</option>
              <option value="admin">Adminisztrátor</option>
              <option value="member">Tag</option>
            </select>
          </div>
        </div>
      </div>

      {/* Organizations Table/List */}
      <div className="crm-card overflow-hidden">
        {paginatedOrganizations.length === 0 ? (
          <div className="text-center py-12">
            <BuildingOffice2Icon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              Nincs találat
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Próbáljon meg más keresési feltételekkel.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Szervezet
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Szerep
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Adószám
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Helyszín
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Tagok
                  </th>
                  {isSuperAdmin && (
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Előfizetés
                    </th>
                  )}
                  <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedOrganizations.map((org) => (
                  <tr
                    key={org.id}
                    onClick={() => handleOpenDetailsModal(org)}
                    className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
                            {org.logoUrl ? <img src={org.logoUrl} alt="" className="h-8 w-8 object-contain" /> : <BuildingOffice2Icon className="h-4 w-4 text-gray-500 dark:text-gray-300" />}
                          </div>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {org.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {getRoleBadge(org)}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {org.taxNumber || '-'}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {org.address?.city ? `${org.address.city}, ${org.address.country}` : '-'}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {(org.users?.length || 0) + 1}
                    </td>
                    {isSuperAdmin && (
                      <td className="px-4 py-2.5 whitespace-nowrap">{subChip(org.id)}{org.isActive === false && <span className="ml-1.5 text-[11px] text-rose-600">· felfüggesztve</span>}</td>
                    )}
                    <td className="px-4 py-2.5 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDetailsModal(org);
                          }}
                          className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Részletek"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(org.id);
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white dark:bg-gray-800 px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Előző
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Következő
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span>
                {' '}-{' '}
                <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, filteredOrganizations.length)}</span>
                {' '}/ {' '}
                <span className="font-medium">{filteredOrganizations.length}</span>
                {' '}szervezet
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === i + 1
                        ? 'z-10 bg-primary-50 dark:bg-primary-900/30 border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Új szervezet */}
      <Modal isOpen={isCreateModalOpen} onClose={handleCloseCreateModal} title={editingOrg ? t('organizations.buttons.edit') : t('organizations.buttons.new')} size="md">
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">{t('organizations.fields.name')} <span className="text-rose-500">*</span></label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ name: e.target.value })} className="w-full h-10 border rounded-lg px-3 text-sm" autoFocus required />
            <p className="text-[11px] text-gray-400 mt-1">A többi adatot (adószám, cím, logó, tagok) létrehozás után a szervezet lapján tudod megadni.</p>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={handleCloseCreateModal} className="crm-btn crm-btn-ghost">{t('organizations.buttons.cancel')}</button>
            <button type="submit" className="crm-btn crm-btn-primary">{editingOrg ? t('organizations.buttons.saveChanges') : t('organizations.buttons.create')}</button>
          </div>
        </form>
      </Modal>

      {/* Szervezet részletei – füles */}
      {selectedOrg && (
        <Modal isOpen={isDetailsModalOpen} onClose={handleCloseDetailsModal} title={selectedOrg.name} size="4xl" noPadding>
          <div className="flex flex-col min-h-full">
            <div className="px-4 sm:px-6 pt-4 pb-3 border-b border-[var(--crm-border)] flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                {selectedOrg.logoUrl ? <img src={selectedOrg.logoUrl} alt="" className="h-10 w-10 object-contain" /> : <BuildingOffice2Icon className="h-5 w-5 text-gray-500 dark:text-gray-300" />}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate">{selectedOrg.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-2">{getRoleBadge(selectedOrg)}<span>{selectedOrg.taxNumber || 'nincs adószám'}</span><span>· {(selectedOrg.users?.length || 0) + 1} tag</span></div>
              </div>
            </div>
            <div className="flex-1 p-4 sm:p-6">
              <OrganizationSettings
                organization={selectedOrg}
                isEditing={true}
                onEditingChange={() => {}}
                onOrganizationUpdate={(updatedOrg) => { handleOrganizationUpdate(updatedOrg); setSelectedOrg(updatedOrg); }}
                hideButtons={true}
                onRequestDelete={(org) => setOrgToDeleteObj(org)}
              />
            </div>
            <div className="mt-auto sticky bottom-0 z-10 border-t border-[var(--crm-border)] bg-[var(--crm-surface)] px-4 sm:px-6 py-3 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))] flex justify-end gap-2">
              <button type="button" onClick={handleCloseDetailsModal} className="crm-btn crm-btn-ghost mr-auto">Bezárás</button>
              <button type="button" onClick={handleSaveChanges} className="crm-btn crm-btn-primary">Mentés</button>
            </div>
          </div>
        </Modal>
      )}

      <DeleteOrganizationModal
        organization={orgToDeleteObj}
        isOpen={!!orgToDeleteObj}
        onClose={() => setOrgToDeleteObj(null)}
        onDeleted={() => { setOrgToDeleteObj(null); setIsDetailsModalOpen(false); setSelectedOrg(null); refetch(); }}
      />

      {/* Confirm Delete Modal (régi, már nem használt út) */}
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setOrgToDelete(null);
        }}
        onConfirm={confirmDelete}
        title={t('organizations.messages.confirmTitle') || "Szervezet törlése"}
        deleteMessage={t('organizations.messages.deleteQuestion') || "Biztosan törölni szeretné ezt a szervezetet?"}
        deleteWarning={t('organizations.messages.deleteWarning') || "Ez a művelet nem visszavonható."}
      />
    </div>
  );
};

export default OrganizationList;
